"use client";

import * as React from "react";
import { Popover as MantinePopover } from "@mantine/core";
import { GripVertical, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SearchInput } from "@/components/shared/SearchInput";
import type { UseResponsivePopoverDragResult } from "./types";

interface DesktopPopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  align: "start" | "center" | "end";
  popoverSide: "top" | "right" | "bottom" | "left";
  draggable: boolean;
  showCloseButton: boolean;
  searchable: boolean;
  searchPlaceholder: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  offset: number;
  withinPortal: boolean;
  drag: UseResponsivePopoverDragResult;
}

/**
 * Desktop popover rendering branch of ResponsivePopover. Uses Mantine Popover
 * because Radix UI Popover has issues escaping Mantine's modal structure.
 */
export const DesktopPopover: React.FC<DesktopPopoverProps> = ({
  children,
  content,
  open,
  onOpenChange,
  className,
  align,
  popoverSide,
  draggable,
  showCloseButton,
  searchable,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  offset,
  withinPortal,
  drag,
}) => {
  const mantineZIndex =
    className?.includes("z-[99999]") || className?.includes("z-[9999]") ? 99999 : 1100;

  // Map align prop to Mantine's position format (e.g., "bottom-start", "bottom-center", "bottom-end")
  const mantinePosition = React.useMemo(() => {
    if (popoverSide === "bottom") {
      if (align === "start") return "bottom-start";
      if (align === "end") return "bottom-end";
      return "bottom";
    }
    if (popoverSide === "top") {
      if (align === "start") return "top-start";
      if (align === "end") return "top-end";
      return "top";
    }
    if (popoverSide === "left") {
      if (align === "start") return "left-start";
      if (align === "end") return "left-end";
      return "left";
    }
    if (popoverSide === "right") {
      if (align === "start") return "right-start";
      if (align === "end") return "right-end";
      return "right";
    }
    return popoverSide;
  }, [popoverSide, align]);

  const { isDragging, position, dragHandleRef, handleMouseDown } = drag;

  return (
    <MantinePopover
      opened={open}
      onChange={onOpenChange}
      position={mantinePosition}
      withArrow={false}
      withinPortal={withinPortal}
      zIndex={mantineZIndex}
      shadow="md"
      offset={offset}
      returnFocus={false}
      trapFocus={false}
    >
      <MantinePopover.Target>{children}</MantinePopover.Target>
      <MantinePopover.Dropdown
        className={cn(
          "max-h-[80vh] overflow-y-auto overflow-x-hidden select-text pointer-events-auto overscroll-contain",
          className?.includes("p-0") ? "" : "p-4",
          className
        )}
        style={{
          ...(draggable
            ? {
                transform: `translate(${position.x}px, ${position.y}px)`,
                cursor: isDragging ? "grabbing" : "default",
              }
            : {}),
          zIndex: mantineZIndex,
        }}
      >
        {(draggable || showCloseButton) && ( // z-index-allow: corner-pinned drag/close cluster inside the dropdown's own box; proven un-clipped by tests/ct/desktop-popover.spec.tsx
          <div className="absolute right-2 top-2 flex gap-1 z-10">
            {draggable && (
              <button
                ref={dragHandleRef}
                onMouseDown={handleMouseDown}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none cursor-grab active:cursor-grabbing p-1"
                type="button"
              >
                <GripVertical className="h-3 w-3" />
                <span className="sr-only">Drag</span>
              </button>
            )}
            {showCloseButton && (
              <button
                onClick={() => onOpenChange?.(false)}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none p-1"
                type="button"
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Close</span>
              </button>
            )}
          </div>
        )}
        <div className={draggable || showCloseButton ? "pr-8" : ""}>
          {searchable && (
            <div className="mb-3">
              <SearchInput
                value={searchValue || ""}
                onChange={(val) => onSearchChange?.(val)}
                placeholder={searchPlaceholder}
                autoFocus
              />
            </div>
          )}
          {content}
        </div>
      </MantinePopover.Dropdown>
    </MantinePopover>
  );
};
